import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { Icon } from './Icon'

/**
 * `continue-button` — the only primary button in the design, on 28 screens
 * (design-spec.md §5.3): height 58, radius 18, `#2C5DFF`, gap 10, centred,
 * label 15/Bold, with an optional 18px arrow to the label's left.
 *
 * `secondary` is the `white/6%` surface the design uses for both the
 * lower-priority action ("گفتگو با پشتیبانی", screen 17) and the disabled
 * state of the primary — they are the same treatment, which is why disabled
 * here is a fill change rather than an opacity change.
 *
 * `invert` is the splash CTA (screen 1), white on radius 28 at 56 high.
 * `card` is the shorter in-card action on the tier screen: 44 high, radius 10,
 * label 15/Bold at 14.
 */
type Variant = 'primary' | 'secondary' | 'invert'

interface PrimaryButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  variant?: Variant
  /** `card` is the 44-high in-card action; `full` is the standard 58. */
  size?: 'full' | 'card'
  withArrow?: boolean
  loading?: boolean
}

const VARIANT: Record<Variant, string> = {
  primary: 'bg-primary text-ink',
  secondary: 'bg-surface-6 text-ink-50',
  invert: 'bg-white text-splash-ink',
}

export function PrimaryButton({
  children,
  variant = 'primary',
  size = 'full',
  withArrow = false,
  loading = false,
  disabled,
  className = '',
  ...rest
}: PrimaryButtonProps) {
  const isDisabled = disabled || loading
  return (
    <button
      type="button"
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={[
        'flex w-full items-center justify-center gap-[10px] transition-colors duration-(--sx-dur)',
        size === 'card' ? 'h-[44px] rounded-10 text-14' : 'text-15',
        size === 'full' && variant === 'invert' ? 'h-[56px] rounded-28' : '',
        size === 'full' && variant !== 'invert' ? 'h-button rounded-18' : '',
        'font-bold',
        isDisabled ? VARIANT.secondary : VARIANT[variant],
        isDisabled ? 'cursor-default' : 'active:opacity-90',
        className,
      ].join(' ')}
      {...rest}
    >
      {/* Label first: in RTL that puts it on the right with the arrow to its
          left, which is how every continue-button in the design is drawn. */}
      <span className="sx-tight">{children}</span>
      {withArrow ? <Icon name="arrow-left" size={18} /> : null}
    </button>
  )
}
